import { api } from '@/api';
import {
  ISingleSystemSetupParameter,
  BeamQualityContainerDto,
  BeamShapingContainerDto,
  instanceOfISingleSystemSetupParameter,
  TaskTypeDescription,
  MedicalSystemSetupBaseDto,
} from '@/api/generator';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { truthy } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { cloneDeep, set, get } from 'lodash';
import { Ref, ref } from 'vue';
import { getBeamShapingTemplate } from './beamShaping/utils';

type ParameterWithUnit = ISingleSystemSetupParameter & {
  unitAbbreviation?: string | null;
};

const selectableItemTypes = ['Single', 'ContainerHeader'] as const;
type SelectableItemType = (typeof selectableItemTypes)[number];

type ItemType = SelectableItemType | 'ContainerElement';

export interface SetupParameterTableEntry {
  itemType: ItemType;
  selected: Ref<boolean>;
  isRequired: boolean;
  name?: I18nKey;
  iuid?: string;
  unit?: string;
  path?: string;
  parameter?: ISingleSystemSetupParameter;
}

export interface SelectableSetupParameterTableEntry extends SetupParameterTableEntry {
  path: string;
  itemType: SelectableItemType;
}

export function isSelectableSetupParameterTableEntry(
  entry: SetupParameterTableEntry,
): entry is SelectableSetupParameterTableEntry {
  const isSelectableEntry = (<readonly ItemType[]>selectableItemTypes).includes(entry.itemType);
  const hasPath = !!entry.path;

  return isSelectableEntry && hasPath;
}

function isParameterWithUnit(param: ISingleSystemSetupParameter): param is ParameterWithUnit {
  return 'unitAbbreviation' in param;
}

async function getMedicalSystemSetupTemplate(
  taskType: TaskTypeDescription,
  targetSystemId?: string,
): Promise<MedicalSystemSetupBaseDto | undefined> {
  const { result: medicalSystemSetupTemplate } =
    await api.taskItemsApi.getMedicalSystemSetupTemplate({
      getMedicalSystemSetupTemplate: {
        targetSystemId,
        taskType,
      },
    });

  const beamShapingContainerTemplate = medicalSystemSetupTemplate
    ? ((await getBeamShapingTemplate(medicalSystemSetupTemplate, taskType)) ?? {})
    : {};

  const template = medicalSystemSetupTemplate
    ? { ...medicalSystemSetupTemplate, ...beamShapingContainerTemplate }
    : undefined;

  return template;
}

export function createEntryForSingleSetupParameter(
  parameter: ISingleSystemSetupParameter,
  defaults: Partial<SetupParameterTableEntry> = {},
): SetupParameterTableEntry {
  return {
    ...defaults,
    itemType: 'Single',
    selected: ref(!!parameter.isEnabled),
    isRequired: false,
    iuid: parameter.iuid ?? undefined,
    unit: isParameterWithUnit(parameter)
      ? (parameter.unitAbbreviation ?? EMPTY_FIELD_SIGN)
      : EMPTY_FIELD_SIGN,
    parameter,
  };
}

export function createEntriesForBeamQualityContainer(
  container: BeamQualityContainerDto,
  defaults: Partial<SetupParameterTableEntry> = {},
): SetupParameterTableEntry[] {
  const containerHeaderEntry: SetupParameterTableEntry = {
    ...defaults,
    selected: ref(!!container.isEnabled),
    isRequired: !!container.isRequired,
    name: container.name as I18nKey,
    itemType: 'ContainerHeader',
  };

  const electron = container.beamQualitiesElectron?.[0];
  const photon = container.beamQualitiesPhoton?.[0];

  const containerElementEntries: SetupParameterTableEntry[] = [
    electron?.radiationType,
    photon?.energy,
    electron?.energy,
    photon?.primaryFluenceShaping,
    photon?.highDoseTechniqueType,
    photon?.selectedDoseRates,
  ]
    .filter(truthy)
    .map((parameter) =>
      createEntryForSingleSetupParameter(parameter, { itemType: 'ContainerElement' }),
    );

  return [containerHeaderEntry, ...containerElementEntries];
}

export function createEntriesForBeamShapingContainer(
  container: BeamShapingContainerDto,
  defaults: Partial<SetupParameterTableEntry> = {},
): SetupParameterTableEntry[] {
  const containerHeaderEntry: SetupParameterTableEntry = {
    ...defaults,
    selected: ref(!!container.isEnabled),
    isRequired: !!container.isRequired,
    name: container.name as I18nKey,
    itemType: 'ContainerHeader',
  };

  const containerElementEntries: SetupParameterTableEntry[] = Object.values(
    container.availableBeamShaping ?? {},
  )
    .filter(instanceOfISingleSystemSetupParameter)
    .map((parameter) =>
      createEntryForSingleSetupParameter(parameter, { itemType: 'ContainerElement' }),
    );

  return [containerHeaderEntry, ...containerElementEntries];
}

function updateEntry(
  medicalSystemSetup: MedicalSystemSetupBaseDto,
  entry: SelectableSetupParameterTableEntry,
) {
  return set(medicalSystemSetup, `${entry.path}.isEnabled`, entry.selected.value);
}

function resetEntry(
  medicalSystemSetup: MedicalSystemSetupBaseDto,
  entry: SelectableSetupParameterTableEntry,
  template?: MedicalSystemSetupBaseDto,
) {
  return set(medicalSystemSetup, entry.path, get(template, entry.path));
}

export async function updateSelection(
  entries: SetupParameterTableEntry[],
  medicalSystemSetup: MedicalSystemSetupBaseDto,
  taskType: TaskTypeDescription,
) {
  const updatedMedicalSystemSetup = cloneDeep(medicalSystemSetup);

  const template = await getMedicalSystemSetupTemplate(
    taskType,
    medicalSystemSetup?.targetSystemEntityId,
  );

  entries.filter(isSelectableSetupParameterTableEntry).forEach((entry) => {
    const isEnabled = entry.selected.value;

    if (isEnabled) {
      updateEntry(updatedMedicalSystemSetup, entry);
    } else {
      resetEntry(updatedMedicalSystemSetup, entry, template);
    }
  });

  return updatedMedicalSystemSetup;
}
